import React, { useEffect, useState } from 'react';
import { BsArrowLeftShort, BsArrowRightShort } from 'react-icons/bs';
import { useHistory } from 'react-router-dom';

import { saveOnBoardData } from 'api/onBoarding';
import { getImageInfo, uploadImage, imageProcess } from 'api/image';
import PhotoUploader from '../PhotoCropper/ImageUploader';
import PhotoCropper from '../PhotoCropper/ImageCropper';
import { getPutFormat } from './filter';
import './styles.scss';

const Step3 = ({ formData }) => {
  const history = useHistory();
  const [openImageCropper, setImageCropperOpen] = useState(false);
  const [originalImg, setOriginalImage] = useState(null);
  const [uploadingImg, setUploadingImage] = useState(null);
  const [imageName, setImageName] = useState('');
  const [logoImageUrl, setLogoImage] = useState(null);
  const [logoImageKey, setLogoImgKey] = useState(null);

  const [isSubmitted, setFormSubmitted] = useState(false);
  const [isImgInvalid, setImgInvalid] = useState(false);
  const [imgLoadStatus, setImageLoadStatus] = useState(false);

  useEffect(() => {
    const { companyInfo } = formData;
    if (companyInfo) {
      setLogoImage(companyInfo.logoImageUrl || null);
      setLogoImgKey(companyInfo.logoImageKey);
    }
  }, [formData]);

  useEffect(() => {
    let timer;
    if (imgLoadStatus === 'success') {
      timer = setTimeout(() => {
        setImageLoadStatus('');
      }, 1000);
    }

    return () => clearTimeout(timer);
  }, [imgLoadStatus]);

  const onDropFile = (acceptedPhoto, filename) => {
    setImageCropperOpen(true);
    setImageName(filename);
    setUploadingImage(acceptedPhoto);
  };

  const onSaveCompanyLogo = async (
    fullImg,
    croppedImg,
    imageBinary,
    hashcode,
  ) => {
    setOriginalImage(fullImg);
    setUploadingImage(null);
    setLogoImage(croppedImg);

    // Validation of the image info
    const infoResponse = await getImageInfo({
      path: 'logo',
      header: hashcode,
      filename: imageName,
    });
    const { status, data } = infoResponse;

    if (status === 200) {
      setImageLoadStatus('loading');
      // Image Uploading to S3 bucket
      const url = data.form.action;
      const { fields } = data.form;
      const uploadResponse = await uploadImage(url, fields, imageBinary);

      if (uploadResponse.status === 204) {
        // Send the image upload status value
        const response = await imageProcess({
          path: 'logo',
          key: uploadResponse.key,
          method: 'post',
        });
        if (response.status === 200) {
          setImageLoadStatus('success');
          setLogoImgKey(uploadResponse.key);
        } else {
          setImageLoadStatus('failed');
        }
      } else {
        setImageLoadStatus('failed');
      }
    } else {
      setImageLoadStatus('failed');
    }
  };

  const onRemoveCompanyLogo = () => {
    setLogoImage(null);
    setOriginalImage(null);
    setLogoImgKey(null);
  };

  const isFormFieldsValid = () => {
    return true;
  };

  const saveForms = async () => {
    const newFormData = formData;
    newFormData.companyInfo.logoImageUrl = logoImageUrl;
    newFormData.companyInfo.logoImageKey = logoImageKey;

    const result = await saveOnBoardData({ boarding: getPutFormat(newFormData) });
    return result;
  };

  const handleBack = () => {
    history.push('/app/v2/account-setup/step/2');
  };

  const handleNext = async () => {
    setFormSubmitted(true);
    if (isFormFieldsValid()) {
      await saveForms();
      history.push('/app/v2/account-setup/step/4');
    }
  };

  return (
    <>
      <PhotoCropper
        src={uploadingImg || originalImg}
        imageUploadType='logo'
        open={openImageCropper}
        onClose={() => {
          setImageCropperOpen(false);
        }}
        onSave={onSaveCompanyLogo}
      />
      <section className="stepflow_content">
        <article className="stepflow_form">
          <section className="stepflow_form-header">
            <h3 className="form-title">Upload your logo.</h3>
            <p className="form-subtitle text-center">
              Build brand recognition and put forward a polished and
              professional appearance. Impress your clients right from the
              start. Don't have a logo handy? You can always upload your logo
              later in your settings.
            </p>
          </section>
          <div className="stepflow_form__uploader">
            <PhotoUploader
              src={logoImageUrl}
              imageUploadType='logo'
              handleOnDrop={onDropFile}
              handleDelete={onRemoveCompanyLogo}
              setImageError={(value) => setImgInvalid(value)}
              imageUploadText='Drag your logo to replace'
            />
          </div>
          {logoImageUrl && originalImg && (
            <div className="mt-2 pb-1">
              <span
                className="adjust-button"
                onClick={() => {
                  setImageCropperOpen(true);
                }}
              >
                Adjust Image
              </span>
            </div>
          )}
        </article>
      </section>
      <section className="stepflow_footer">
        <button className="stepflow_footer-button"
          onClick={handleBack}
        >
          <BsArrowLeftShort size={20} /> Back
        </button>
        {isSubmitted && !isFormFieldsValid() && (
          <div className="stepflow_footer-error">
            Please complete required fields above.
          </div>
        )}
        {isImgInvalid && (
          <div className="stepflow_footer-error">
            Please upload your image in one of the following formats: JPEG or
            PNG.
          </div>
        )}
        {!isImgInvalid && imgLoadStatus === 'loading' && (
          <div className="stepflow_footer-error">Updating Company Logo...</div>
        )}
        {!isImgInvalid && imgLoadStatus === 'success' && (
          <div className="stepflow_footer-error">
            Updated Company Logo Successfully
          </div>
        )}
        {!isImgInvalid && imgLoadStatus === 'failed' && (
          <div className="stepflow_footer-error">
            Updating Company Logo Failed
          </div>
        )}
        <button
          className="stepflow_footer-button"
          color="primary"
          onClick={handleNext}
        >
          Next
          <BsArrowRightShort size={20} />
        </button>
      </section>
    </>
  );
};

export default Step3;
